import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  ArrowRight, Sparkles, Timer, Brain, BookOpen, Trophy, Zap,
  CheckCircle2, Bookmark, Radar, ChevronRight, Play,
} from "lucide-react";
import Math from "@/components/Math";

const RotatingWord = () => {
  const words = ["understood.", "practised.", "aced.", "loved."];
  return (
    <span className="ticker-mask">
      <span className="ticker-track text-yellow-300">
        {[...words, words[0]].map((w, i) => (
          <span key={i} className="block">{w}</span>
        ))}
      </span>
    </span>
  );
};

const Stat = ({ n, label }) => (
  <div className="flex flex-col">
    <div className="font-display text-4xl font-bold shimmer-yellow">{n}</div>
    <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">{label}</div>
  </div>
);

const ChapterChip = ({ cls, num, title }) => (
  <div className="chip cursor-default flex items-center gap-3 rounded-md border border-white/10 bg-[#152040] px-3 py-2.5">
    <div className="text-[10px] uppercase tracking-[0.15em] text-pink-300 font-mono">C{cls}·{num}</div>
    <div className="text-sm text-zinc-200 truncate">{title}</div>
  </div>
);

const FeatureBig = ({ icon: Icon, title, desc, tag, testid }) => (
  <div data-testid={testid} className="lift relative rounded-lg border border-white/10 bg-[#152040] p-7 overflow-hidden">
    <div className="absolute right-6 top-6 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{tag}</div>
    <div className="w-12 h-12 rounded-md bg-orange-300/10 border border-yellow-400/30 flex items-center justify-center mb-5">
      <Icon className="w-5 h-5 text-yellow-300" />
    </div>
    <div className="font-display text-xl font-semibold mb-2 tracking-tight">{title}</div>
    <div className="text-sm text-zinc-400 leading-relaxed">{desc}</div>
  </div>
);

const HowStep = ({ n, title, desc }) => (
  <div className="flex gap-5 items-start">
    <div className="font-display text-4xl font-bold text-yellow-300 leading-none w-14 shrink-0">{n}</div>
    <div>
      <div className="font-display text-lg font-semibold mb-1">{title}</div>
      <div className="text-sm text-zinc-400 leading-relaxed">{desc}</div>
    </div>
  </div>
);

const Quote = ({ name, meta, text }) => (
  <div className="rounded-lg border border-white/10 bg-[#152040] p-6 lift">
    <div className="text-yellow-300 font-display text-3xl leading-none mb-3">"</div>
    <div className="text-sm text-zinc-200 leading-relaxed mb-4">{text}</div>
    <div className="flex items-center gap-2 text-xs text-zinc-500">
      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-300 to-orange-300 text-slate-900 grid place-items-center font-bold text-[10px]">{name[0]}</div>
      <span className="text-zinc-300">{name}</span>
      <span>·</span>
      <span>{meta}</span>
    </div>
  </div>
);

export default function Landing() {
  const { loginWithGoogle, user } = useAuth();
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    // preview chapter chips for the marquee — deterministic hardcoded picks
    setClasses([
      { c: "10", n: "01", t: "Real Numbers" },
      { c: "10", n: "04", t: "Quadratic Equations" },
      { c: "10", n: "08", t: "Intro to Trigonometry" },
      { c: "9", n: "02", t: "Polynomials" },
      { c: "9", n: "10", t: "Heron's Formula" },
      { c: "10", n: "05", t: "Arithmetic Progressions" },
      { c: "8", n: "04", t: "Squares & Square Roots" },
      { c: "7", n: "06", t: "The Triangle" },
      { c: "9", n: "09", t: "Circles" },
      { c: "10", n: "13", t: "Statistics" },
      { c: "6", n: "05", t: "Fractions" },
      { c: "10", n: "14", t: "Probability" },
    ]);
  }, []);

  const primaryCta = () => (user ? (
    <Link to="/dashboard">
      <Button data-testid="hero-cta-dashboard" size="lg" className="bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold px-6">
        Go to your dashboard <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
    </Link>
  ) : (
    <Button data-testid="hero-cta-google" size="lg" onClick={loginWithGoogle} className="bg-sky-400 text-slate-900 hover:bg-sky-300 rounded-full font-semibold px-6">
      Start free with Google <ArrowRight className="w-4 h-4 ml-2" />
    </Button>
  ));

  return (
    <div className="-mt-8 -mx-4 sm:-mx-6">
      {/* ----- HERO ----- */}
      <section className="relative overflow-hidden">
        <div className="aurora aurora-1" />
        <div className="aurora aurora-2" />
        <div className="aurora aurora-3" />
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 fade-up">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur-md px-3 py-1 text-xs text-zinc-300 mb-6" data-testid="hero-tag">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Now covering CBSE Classes 6-12 · Sept 2026
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1] mb-6" data-testid="hero-title">
              CBSE Maths, actually <br className="hidden sm:block" /><RotatingWord />
            </h1>
            <p className="text-lg text-zinc-400 max-w-xl mb-8 leading-relaxed">
              NCERT chapters. Every solved example. Twice-a-day quizzes. A mental-maths sprint. An AI tutor that explains problems the way your favourite teacher does — patiently, step by step.
            </p>
            <div className="flex flex-wrap gap-3 items-center">
              {primaryCta()}
              <Link to="/classes">
                <Button data-testid="hero-cta-explore" size="lg" variant="outline" className="rounded-full border-white/20 text-white bg-transparent hover:bg-white/5">
                  <Play className="w-4 h-4 mr-2" /> Peek inside
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-4 gap-8 mt-12 max-w-lg">
              <Stat n="7" label="Classes 6-12" />
              <Stat n="44" label="NCERT chapters" />
              <Stat n="2×" label="Daily quizzes" />
              <Stat n="AI" label="Doubt solver" />
            </div>
          </div>

          {/* Right: live-solve card + floating chapter chip */}
          <div className="md:col-span-5 fade-up fade-up-2 relative">
            <div className="floaty absolute -top-6 -right-2 z-10 hidden md:block">
              <div className="rounded-full border border-orange-300/40 bg-orange-300/10 backdrop-blur-md px-3 py-1.5 text-xs text-yellow-300 flex items-center gap-2">
                <Sparkles className="w-3 h-3" /> Class 10 · Trigonometry unlocked
              </div>
            </div>
            <div className="relative rounded-xl border border-white/10 bg-[#0f0f12] overflow-hidden shadow-2xl">
              <div className="bp-grid absolute inset-0 opacity-60" />
              <div className="relative p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-pink-300">Live sample · Class 10</div>
                  <div className="flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-red-500/60" />
                    <span className="w-2 h-2 rounded-full bg-orange-300/60" />
                    <span className="w-2 h-2 rounded-full bg-green-500/60" />
                  </div>
                </div>
                <div className="font-display text-2xl font-bold mb-5 leading-snug">Solve <Math>{`$2x^2 - 7x + 3 = 0$`}</Math></div>
                <div className="space-y-2 font-mono text-sm">
                  <div className="flex gap-3 fade-up"><span className="text-yellow-300">01</span><span className="text-zinc-300 bp-math"><Math>{`Product $ac = 6$, split $-7 \\to -6$ and $-1$`}</Math></span></div>
                  <div className="flex gap-3 fade-up fade-up-1"><span className="text-yellow-300">02</span><span className="text-zinc-300 bp-math"><Math>{`$2x^2 - 6x - x + 3 = 0$`}</Math></span></div>
                  <div className="flex gap-3 fade-up fade-up-2"><span className="text-yellow-300">03</span><span className="text-zinc-300 bp-math"><Math>{`$2x(x - 3) - 1(x - 3) = 0$`}</Math></span></div>
                  <div className="flex gap-3 fade-up fade-up-3"><span className="text-yellow-300">04</span><span className="text-zinc-300 bp-math"><Math>{`$(2x - 1)(x - 3) = 0$`}</Math></span></div>
                  <div className="flex gap-3 mt-3 pt-3 border-t border-white/10">
                    <CheckCircle2 className="w-4 h-4 text-pink-300 mt-0.5" />
                    <span className="text-pink-300"><Math>{`$x = \\tfrac{1}{2}$ or $x = 3$`}</Math></span>
                  </div>
                </div>
              </div>
              <div className="relative border-t border-white/5 bg-black/40 px-6 py-3 flex items-center justify-between text-xs text-zinc-500">
                <span className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-yellow-300" /> Gyaan · AI Tutor</span>
                <span className="font-mono">1.2s</span>
              </div>
            </div>
            <div className="floaty absolute -bottom-4 -left-4 z-10 hidden md:block" style={{ animationDelay: "1.2s" }}>
              <div className="rounded-md border border-white/10 bg-black/70 backdrop-blur-md px-3 py-2 text-xs text-zinc-300 flex items-center gap-2">
                <Bookmark className="w-3 h-3 text-yellow-300" /> 42 saved by students today
              </div>
            </div>
          </div>
        </div>

        {/* chapter marquee */}
        <div className="border-y border-white/5 bg-black/40 backdrop-blur-sm py-4 overflow-hidden">
          <div className="marquee gap-3 px-3">
            {[...classes, ...classes].map((c, i) => (
              <ChapterChip key={i} cls={c.c} num={c.n} title={c.t} />
            ))}
          </div>
        </div>
      </section>

      {/* ----- FEATURES ----- */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12 flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">What's inside</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Made for the real board exam.</h2>
          </div>
          <div className="text-sm text-zinc-500 max-w-sm">Every feature is designed with the CBSE marking scheme, NCERT syllabus, and Indian classroom in mind.</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureBig testid="feature-chapters" icon={BookOpen} title="Full NCERT chapters" tag="01" desc="Every chapter from Class 6 to 12 — theory, worked examples, and detailed solutions to every exercise question." />
          <FeatureBig testid="feature-solutions" icon={Sparkles} title="Step-by-step solutions" tag="02" desc="Solutions broken down algebraically with a final verification line — no skipped steps, no hand-waving." />
          <FeatureBig testid="feature-daily-quiz" icon={Timer} title="Twice-a-day quizzes" tag="03" desc="A fresh 5-question quiz at morning and evening. Rotate topics, build streaks, keep your edge sharp." />
          <FeatureBig testid="feature-mental" icon={Brain} title="Mental maths sprint" tag="04" desc="10 rapid-fire questions in 60 seconds. Sharpen the speed the board demands in the very first section." />
          <FeatureBig testid="feature-ai" icon={Zap} title="AI doubt-solver" tag="05" desc="Ask Gyaan any problem — even from your own textbook — and get a structured, teacher-style walkthrough instantly." />
          <FeatureBig testid="feature-radar" icon={Radar} title="Topic-accuracy radar" tag="06" desc="See exactly which chapters you're weak in with a personal radar chart. Fix what matters most before the paper." />
        </div>
      </section>

      {/* ----- HOW IT WORKS ----- */}
      <section className="border-y border-white/5 bg-[#0b1428]">
        <div className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">How it works</div>
            <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-4">Three steps to a topper's routine.</h2>
            <p className="text-zinc-400 leading-relaxed mb-8">A tight loop that fits in 15 minutes a day — before school, after tuition, whenever you like.</p>
            <div className="rounded-lg border border-white/10 bg-[#152040] p-5">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Today's target</div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-display text-xl font-bold">1 chapter · 1 quiz · 1 sprint</div>
                  <div className="text-xs text-zinc-500">About 15 minutes</div>
                </div>
                <div className="font-display text-3xl gradient-streak">15m</div>
              </div>
              <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-pink-300 via-orange-300 to-yellow-300" style={{ width: "60%" }} />
              </div>
            </div>
          </div>
          <div className="md:col-span-7 space-y-8">
            <HowStep n="01" title="Pick your class and chapter" desc="Browse NCERT-aligned chapters for your class. Every chapter opens with a clean summary and key concepts you can revise in 2 minutes." />
            <HowStep n="02" title="Practise with the exercise" desc="Every question ships with a step-by-step solution. Stuck? Tap Show Solution or ask the AI Tutor for a fresh walkthrough." />
            <HowStep n="03" title="Nail the daily quiz + sprint" desc="Take the morning or evening quiz, then hit the 60-second mental-maths sprint. Your streak and topic radar update automatically." />
          </div>
        </div>
      </section>

      {/* ----- CLASSES SHOWCASE ----- */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="mb-12">
          <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Coverage</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold tracking-tight">Class 6 to Class 10. All in.</h2>
        </div>
        <div className="grid md:grid-cols-5 gap-3">
          {[
            { c: "6", n: 6, hint: "Where it starts" },
            { c: "7", n: 6, hint: "Algebra muscle" },
            { c: "8", n: 6, hint: "Pieces click" },
            { c: "9", n: 12, hint: "Board foundation" },
            { c: "10", n: 14, hint: "Board year" },
          ].map((k, i) => (
            <Link
              key={k.c}
              to={`/classes/${k.c}`}
              data-testid={`landing-class-${k.c}`}
              className={`lift relative rounded-lg border border-white/10 bg-[#152040] p-5 overflow-hidden fade-up`}
              style={{ animationDelay: `${i * 60}ms` }}
            >
              <div className="text-[11px] uppercase tracking-[0.2em] text-pink-300 mb-2">CBSE · NCERT</div>
              <div className="font-display text-3xl font-bold">Class {k.c}</div>
              <div className="text-xs text-zinc-500 mt-1 mb-4">{k.hint}</div>
              <div className="flex items-center justify-between text-xs">
                <span className="font-mono text-yellow-300">{k.n} chapters</span>
                <ChevronRight className="w-4 h-4 text-zinc-500" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ----- SOCIAL PROOF ----- */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-10 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-pink-300 mb-2">Loved by students</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">From students preparing right now.</h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Trophy className="w-4 h-4 text-yellow-300" />
            <span>Class 10 board averages up ~14%*</span>
          </div>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <Quote name="Riya" meta="Class 10 · Delhi" text="The step-by-step solutions actually feel like a teacher explaining. Quadratics finally clicked after two attempts." />
          <Quote name="Aarav" meta="Class 9 · Bengaluru" text="Doing the morning quiz on my school bus every day. My streak is 34 and I actually look forward to it now." />
          <Quote name="Meera" meta="Class 10 · Pune" text="The topic radar showed me I was slacking on trigonometry. Fixed it in a week using the AI tutor. Board-ready energy." />
        </div>
      </section>

      {/* ----- FINAL CTA ----- */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="stripes relative rounded-2xl border border-yellow-400/30 bg-gradient-to-br from-[#181812] to-[#0f0f0a] p-10 md:p-14 text-center overflow-hidden">
          <div className="relative">
            <h3 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Ready to actually <span className="text-yellow-300">enjoy</span> maths?
            </h3>
            <p className="text-zinc-400 max-w-xl mx-auto mb-8">
              Sign in with Google — no forms, no cards. Start your streak with today's quiz.
            </p>
            <div className="flex justify-center">
              {primaryCta()}
            </div>
            <div className="text-[11px] text-zinc-500 mt-4">Free forever · Progress synced to your Google account</div>
          </div>
        </div>
      </section>
    </div>
  );
}
